// The mobile header's leading slot is the app logo on a tab root and a BACK chevron on a drilled-in screen
// (PO, 2026-09-14: "logo-replacement back buttons, mobile only") — the native leading-nav convention, and one
// consistent affordance in place of the per-screen in-body backs that had drifted (a circular chip on the
// profiles, a bare chevron on the DOS sub-screens).
//
// A drilled-in screen REGISTERS its back handler here and the shell's `MobileHeader` renders the chevron; when
// nothing is registered (a tab root) the header shows the logo. Same shape and reasoning as
// `rosterHeaderAction` / `teamPanel`: UI state settable from any depth, no prop-threading through the router
// Outlet and no second context provider. Registration is tied to the screen's LIFETIME, so the back leaves
// with it.
//
// The DOS detail's "Back to shows" registers here too (2026-09-15) — it flips an in-content sub-view rather
// than navigating a route, but the PO's rule is that EVERY back rides the navbar chevron, so the handler simply
// toggles state instead of calling history. The one thing still OUTSIDE this is the chat takeover, which hides
// the whole header (`isFullScreenRoute`) and keeps its own back. Desktop is untouched — only `MobileHeader`
// consumes this store.
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { setBackSwipeEnabled } from "@cntrl/native";
import { registerStoreReset } from "./stores.js";

interface MobileBackState {
  /** The current drilled-in screen's back handler, or null on a tab root (⇒ the header shows the logo). */
  handler: (() => void) | null;
  set: (handler: () => void) => void;
  clear: () => void;
  reset: () => void;
}

export const useMobileBackStore = create<MobileBackState>((set) => ({
  handler: null,
  set: (handler) => set({ handler }),
  clear: () => set({ handler: null }),
  reset: () => set({ handler: null }),
}));

// Scorch on org/band switch + logout: a back handler registered by the previous tenant's screen must never
// fire over the new one (stores.ts). A screen still mounted re-asserts (below).
registerStoreReset(() => useMobileBackStore.getState().reset());

// iOS EDGE SWIPE-BACK (ART-268): the wrapper's native gesture is on exactly while a back is registered, so a swipe
// is offered wherever the chevron is and nowhere else (a tab root has none). The gesture steps back through the
// web view's HISTORY, which is why EVERY handler registered here must be a history-back (with a parent-route
// fallback for a cold open, where there is no history and the native gesture is inert anyway). A handler that does
// anything else — a fixed route, an in-page state flip — would make the swipe and the chevron land in different
// places. No-op on the web.
useMobileBackStore.subscribe((s) => setBackSwipeEnabled(s.handler !== null));

/**
 * Register this screen's back handler for as long as it is mounted — the shell then draws the chevron in the
 * header's leading slot. Pass `null` (or nothing) for a screen that wants the logo, not a back.
 *
 * The handler rides a REF so a screen whose callback closes over changing state does not re-register on every
 * render; the effect keys on PRESENCE (`active`) alone. It re-asserts across a `resetAllStores()` scorch that
 * empties the store while the screen is still mounted (the `rosterHeaderAction` lesson — an artist switch
 * nulls the store without unmounting the screen), and clears on unmount.
 */
export function useMobileBack(onBack: (() => void) | null | undefined): void {
  const set = useMobileBackStore((s) => s.set);
  const clear = useMobileBackStore((s) => s.clear);
  const handler = useRef(onBack);
  handler.current = onBack;
  const active = onBack != null;

  useEffect(() => {
    if (!active) return;
    const call = () => handler.current?.();
    set(call);
    // While mounted this screen OWNS the slot, including across a scorch that empties it underneath.
    const unsubscribe = useMobileBackStore.subscribe((s) => {
      if (s.handler === null) useMobileBackStore.getState().set(call);
    });
    // Unsubscribe BEFORE clearing, or the listener re-registers the handler this cleanup is retiring.
    return () => {
      unsubscribe();
      clear();
    };
  }, [active, set, clear]);
}
